import { isReference, useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import {
  CREATE_BOOKMARK,
  REMOVE_BOOKMARK,
} from '@/api/mutations/bookmarks.mutations';
import { Ad } from '@/api/types/common.types';
import {
  CreateBookmarkResponse,
  RemoveBookmarkResponse,
} from '@/api/types/ads.types';
import { UserBookmarksCache } from '@/api/types/user.types';
import { useAuthStore } from '@/store/useAuthStore';

export function useBookmark({ ad }: { ad?: Ad }) {
  const [isBookmarked, setIsBookmarked] = useState(false);
  const userId = useAuthStore(state => state.userId);

  useEffect(() => {
    if (ad) {
      setIsBookmarked(ad.isBookmarked);
    }
  }, [ad]);

  const [createBookmark, { loading }] = useMutation<CreateBookmarkResponse>(
    CREATE_BOOKMARK,
    {
      onError: () => setIsBookmarked(false),
      update: (cache, { data }) => {
        if (!ad) return;

        cache.modify({
          id: cache.identify({ __typename: 'Ad', id: ad.id }),
          fields: {
            isBookmarked: () => !!data?.createBookmark?.entity,
          },
          broadcast: true,
        });

        cache.modify<UserBookmarksCache>({
          id: cache.identify({ __typename: 'User', id: userId }),
          fields: {
            bookmarks: oldBookmarks => {
              if (isReference(oldBookmarks)) return oldBookmarks;

              const adRef = cache.identify(ad);
              if (!adRef) return oldBookmarks;

              const isAlreadyExists = oldBookmarks?.nodes?.some(
                // eslint-disable-next-line no-underscore-dangle
                item => item.__ref === adRef
              );
              if (!oldBookmarks || isAlreadyExists) return oldBookmarks;

              return {
                ...oldBookmarks,
                nodes: [{ __ref: adRef }, ...oldBookmarks.nodes],
                totalCount: oldBookmarks.totalCount + 1,
              };
            },
          },
          broadcast: true,
        });
      },
    }
  );

  const [removeBookmark] = useMutation<RemoveBookmarkResponse>(
    REMOVE_BOOKMARK,
    {
      onError: () => setIsBookmarked(true),
      update: (cache, { data }) => {
        if (!ad) return;

        cache.modify({
          id: cache.identify({ __typename: 'Ad', id: ad.id }),
          fields: {
            isBookmarked: () => !data?.destroyBookmark?.entity,
          },
          broadcast: true,
        });

        cache.modify<UserBookmarksCache>({
          id: cache.identify({ __typename: 'User', id: userId }),
          fields: {
            bookmarks: oldBookmarks => {
              if (isReference(oldBookmarks) || !oldBookmarks)
                return oldBookmarks;

              const adRef = cache.identify(ad);
              if (!adRef) return oldBookmarks;

              return {
                ...oldBookmarks,
                // eslint-disable-next-line no-underscore-dangle
                nodes: oldBookmarks.nodes.filter(item => item.__ref !== adRef),
                totalCount: Math.max(oldBookmarks.totalCount - 1, 0),
              };
            },
          },
          broadcast: true,
        });
      },
    }
  );

  async function toggleBookmark() {
    if (!ad) return;

    if (isBookmarked) {
      setIsBookmarked(false);
      await removeBookmark({ variables: { adId: ad.id } });
      return;
    }

    setIsBookmarked(true);
    await createBookmark({ variables: { adIds: [ad.id] } });
  }

  return { toggleBookmark, isBookmarked, loading };
}
